import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import AnimatedBackground from "../AnimatedBackground";
import Container from "../Container";

const FORMSUBMIT_URL = "https://formsubmit.co/ajax/4f54cf3d65f379eaf38be99fdde99313";

const WaitlistPage: React.FunctionComponent = () => {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [helpDesk, setHelpDesk] = useState("");
  const [commsSoftware, setCommsSoftware] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(FORMSUBMIT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email,
          company_name: companyName,
          help_desk_software: helpDesk,
          comms_software: commsSoftware,
          team_size: teamSize,
          _subject: "New Waitlist Signup — Punch Cards",
          _template: "table",
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setError(
          data.message ||
            "Something went wrong submitting the form. Please try again."
        );
      }
    } catch {
      setError(
        "Unable to reach the server. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedBackground>
      <Container>
        <div className="w-full max-w-2xl">
          <Heading level={1} variant="xl" className="mb-8 text-foreground">
            Join the Waitlist
          </Heading>

          <Card className={`bg-card/95 backdrop-blur-sm shadow-xl ${isSubmitted ? "py-12" : ""}`}>
          <CardContent className={isSubmitted ? "px-12" : undefined}>
            {isSubmitted ? (
              <div>
                <Heading level={2} variant="lg" className="mb-3 text-foreground">
                  You&apos;re on the list!
                </Heading>
                <Text variant="md" className="text-muted-foreground">
                  Thanks for joining the waitlist. We&apos;ll be in touch soon with next steps.
                </Text>
              </div>
            ) : (
              <>
                <Text variant="lg" className="text-muted-foreground mb-6">
                  Ready to improve your team&apos;s performance? Join our waitlist and we&apos;ll get your company onboarded as soon as we&apos;re ready.
                </Text>

                {error && (
                  <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4 mb-6">
                    <Text variant="sm" className="text-destructive">
                      {error}
                    </Text>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Company Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name</Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Your company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="helpDesk">Help Desk Software</Label>
                    <Input
                      id="helpDesk"
                      type="text"
                      placeholder="e.g. Intercom, Zendesk, Freshdesk"
                      value={helpDesk}
                      onChange={(e) => setHelpDesk(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commsSoftware">Comms Software</Label>
                    <Input
                      id="commsSoftware"
                      type="text"
                      placeholder="e.g. Slack, Microsoft Teams"
                      value={commsSoftware}
                      onChange={(e) => setCommsSoftware(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="teamSize">No. of Customer Service Agents</Label>
                    <Input
                      id="teamSize"
                      type="number"
                      placeholder="e.g. 12"
                      value={teamSize}
                      onChange={(e) => setTeamSize(e.target.value)}
                      required
                      min="1"
                    />
                  </div>

                  <div>
                    <Button
                      type="submit"
                      variant="highlight"
                      size="xl"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting…" : "Join the Waitlist"}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </CardContent>
          </Card>
        </div>
      </Container>
    </AnimatedBackground>
  );
};

export default WaitlistPage;
